import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import ThumbUpRoundedIcon from '@mui/icons-material/ThumbUpRounded';
import ThumbDownRoundedIcon from '@mui/icons-material/ThumbDownRounded';
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Customized,
} from 'recharts';
import { callApi } from 'src/core/callApi';
import AIOverviewCard from 'src/App/Dashboards/Market/Components/Cards/AIOverviewCard';
import InfoButton from 'src/App/Dashboards/Market/Components/Buttons/InfoButton';
import { GET_HOTEL_DATA_API_URL } from 'src/utils/const';
import {
  COLOR_TEXT_1,
  COLOR_TEXT_2,
  COLOR_GREY_200,
  COLOR_PRIMARY_2,
  COLOR_SECONDARY_2,
  COLOR_SUCCESS_1,
  COLOR_SUCCESS_BACKGROUNG,
  COLOR_ERROR_1,
  COLOR_ERROR_BACKGROUNG,
} from 'src/utils/colors';

// Brand colors: market bar (teal), benchmark = primary brand (darker teal)
const MARKET_BAR_COLOR = '#0097B2';
const NATIONAL_MEDIAN_COLOR = COLOR_PRIMARY_2; // #037A90

const LARGE_HOTEL_TOOLTIP = 'Hotels, motels, and resorts with 30 or more rooms.';
const BOUTIQUE_HOTEL_TOOLTIP = 'Hotels, motels, and resorts with 5 to 30 rooms.';

const STR_OUTPERFORMING = 'Short-term rentals are outperforming hotels in this market relative to global benchmarks.';
const HOTEL_OUTPERFORMING = 'Hotels are outperforming short-term rentals in this market relative to global benchmarks.';

const HOTEL_DEMAND_PRESSURE_TOOLTIP =
  'Hotel Demand Pressure estimates how booked hotels are at each lead time.\n\nIf fewer hotels are available compared to peak inventory, pressure is higher. Higher % = more booked hotels.\n\nThis is not an exact occupancy rate, but a directional metric that allows comparison across markets.';

export const VERIFIED_MARKETS_MESSAGE =
  'This data is only available in verified markets (blue checkmark next to the market name) on the Expert or Scale plan.';

const LEAD_TIME_KEYS = ['1', '7', '14'] as const;
const LEAD_TIME_LABELS: Record<string, string> = {
  '1': '1 day',
  '7': '+7 days',
  '14': '+14 days',
};

export type HotelDataMessage = {
  large_hotel: {
    hotel_count: number;
    hotel_count_per_1000_airbnb_listings: number | null;
    hotel_count_per_1000_airbnb_listings_global_avg: number | null;
  };
  boutique_hotel: {
    hotel_count: number;
    hotel_count_per_1000_airbnb_listings: number | null;
    hotel_count_per_1000_airbnb_listings_global_avg: number | null;
  };
  hotel_demand_level: number | null;
  hotel_demand_level_global_avg: number | null;
  str_vs_hotel: 'STR' | 'Hotel';
  boutique_hotel_availability_by_lead_time: Record<
    string,
    { availability: number; availability_global_avg: number }
  >;
};

const roundInt = (n: number | null | undefined): number =>
  n != null ? Math.round(Number(n)) : 0;

function deriveSupplyRows(data: HotelDataMessage): Array<{
  label: string;
  hotelCount: number;
  perThousand: number;
  nationalAvg: number;
  tooltip: string;
  highLow: 'High' | 'Low';
}> {
  const l = data.large_hotel;
  const b = data.boutique_hotel;
  const lPer = roundInt(l.hotel_count_per_1000_airbnb_listings);
  const lAvg = roundInt(l.hotel_count_per_1000_airbnb_listings_global_avg);
  const bPer = roundInt(b.hotel_count_per_1000_airbnb_listings);
  const bAvg = roundInt(b.hotel_count_per_1000_airbnb_listings_global_avg);
  return [
    {
      label: 'Large hotel',
      hotelCount: l.hotel_count,
      perThousand: lPer,
      nationalAvg: lAvg,
      tooltip: LARGE_HOTEL_TOOLTIP,
      highLow: lPer >= lAvg ? 'High' : 'Low',
    },
    {
      label: 'Boutique hotels',
      hotelCount: b.hotel_count,
      perThousand: bPer,
      nationalAvg: bAvg,
      tooltip: BOUTIQUE_HOTEL_TOOLTIP,
      highLow: bPer >= bAvg ? 'High' : 'Low',
    },
  ];
}

function deriveAvailabilityData(data: HotelDataMessage): Array<{
  leadTime: string;
  market: number;
  nationalMedian: number;
}> {
  return LEAD_TIME_KEYS.map(key => {
    const item = data.boutique_hotel_availability_by_lead_time[key] ?? {
      availability: 0,
      availability_global_avg: 0,
    };
    return {
      leadTime: LEAD_TIME_LABELS[key] ?? key,
      market: roundInt(item.availability),
      nationalMedian: roundInt(item.availability_global_avg),
    };
  });
}

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;
  const delta = data.market - data.nationalMedian;
  const deltaLabel = delta > 0 ? `↑ ${delta}pp vs global` : delta < 0 ? `↓ ${Math.abs(delta)}pp vs global` : 'vs global';
  return (
    <Box
      sx={{
        background: '#fff',
        p: 1.5,
        borderRadius: 1,
        boxShadow: 2,
        border: '1px solid #E0E0E0',
      }}
    >
      <Typography variant="body2" fontWeight={600} sx={{ color: COLOR_TEXT_2, mb: 0.5 }}>
        {data.leadTime}
      </Typography>
      <Typography variant="body2" sx={{ color: MARKET_BAR_COLOR }}>
        Market: {roundInt(data.market)}%
      </Typography>
      <Typography variant="body2" sx={{ color: NATIONAL_MEDIAN_COLOR, fontSize: '0.75rem' }}>
        Global median: {roundInt(data.nationalMedian)}% · {deltaLabel}
      </Typography>
    </Box>
  );
};

// Horizontal benchmark "bar" (short line) at global median per bar, in pixel space
const HORIZONTAL_BAR_HALF_WIDTH_PX = 14;
const BENCHMARK_STROKE_WIDTH = 5;

const BenchmarkHorizontalBars = (props: any) => {
  const { xAxisMap, yAxisMap, data, stroke } = props;
  if (!data?.length) return null;
  const xAxis = xAxisMap?.['0'] ?? Object.values(xAxisMap || {})[0];
  const yAxis = yAxisMap?.['0'] ?? Object.values(yAxisMap || {})[0];
  if (!xAxis?.scale || !yAxis?.scale) return null;
  const xScale = xAxis.scale;
  const yScale = yAxis.scale;
  const bandwidth =
    typeof xScale.bandwidth === 'function' ? xScale.bandwidth() : (xScale as any).bandwidth ?? 32;

  return (
    <g>
      {data.map((row: { leadTime: string; nationalMedian: number }) => {
        const xStartBand = xScale(row.leadTime);
        if (xStartBand == null || xStartBand === undefined) return null;
        const xCenter = xStartBand + bandwidth / 2;
        const x1 = xCenter - HORIZONTAL_BAR_HALF_WIDTH_PX;
        const x2 = xCenter + HORIZONTAL_BAR_HALF_WIDTH_PX;
        const y = yScale(row.nationalMedian);
        if (y == null || y === undefined) return null;
        return (
          <line
            key={row.leadTime}
            x1={x1}
            y1={y}
            x2={x2}
            y2={y}
            stroke={stroke}
            strokeWidth={BENCHMARK_STROKE_WIDTH}
            strokeLinecap="round"
          />
        );
      })}
    </g>
  );
};

const HotelSupplyAndAvailabilityCard = ({
  isMobileView,
  marketId,
  isVerifiedMarket = false,
}: {
  isMobileView: boolean;
  marketId?: number | string;
  isVerifiedMarket?: boolean;
}) => {
  const [hotelData, setHotelData] = useState<HotelDataMessage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isVerifiedMarket) {
      setHotelData(null);
      setLoading(false);
      setError(null);
      return;
    }
    if (marketId == null || marketId === '') {
      setHotelData(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    callApi(
      GET_HOTEL_DATA_API_URL,
      { body: { market_id: marketId } },
      true,
      false,
      '',
      'POST',
      true
    )
      .then((data: { message?: HotelDataMessage; is_user_allowed?: boolean; error?: string }) => {
        if (cancelled) return;
        const payload = data?.message;
        const isUserAllowed = data?.is_user_allowed === true;
        const hasShape =
          payload &&
          typeof payload === 'object' &&
          'large_hotel' in payload &&
          'boutique_hotel' in payload;
        if (hasShape) {
          setHotelData(payload as HotelDataMessage);
          setError(isUserAllowed ? null : VERIFIED_MARKETS_MESSAGE);
        } else {
          setHotelData(null);
          setError((typeof payload === 'string' ? payload : data?.error) || VERIFIED_MARKETS_MESSAGE);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHotelData(null);
          setError(VERIFIED_MARKETS_MESSAGE);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [marketId, isVerifiedMarket]);

  const supplyCardValueColors = [COLOR_SECONDARY_2, MARKET_BAR_COLOR];
  const demandLevelTooltip =
    'Indicates that hotel demand in this market is strong relative to supply and comparable markets.';

  const supplyRows = hotelData ? deriveSupplyRows(hotelData) : [];
  const availabilityData = hotelData ? deriveAvailabilityData(hotelData) : [];

  const showVerifiedMessage =
    !isVerifiedMarket ||
    error ||
    (!loading && !hotelData && marketId != null && marketId !== '');
  const shouldBlur = !!error;
  const blurSx = shouldBlur ? { filter: 'blur(4px)', userSelect: 'none' as const } : {};

  if (!isVerifiedMarket) {
    return (
      <AIOverviewCard isLoading={false} isMobileView={isMobileView}>
        <Typography variant="body2" sx={{ color: COLOR_TEXT_2, mb: 2 }}>
          {VERIFIED_MARKETS_MESSAGE}
        </Typography>
      </AIOverviewCard>
    );
  }

  return (
    <AIOverviewCard isLoading={loading} isMobileView={isMobileView}>
      {showVerifiedMessage && (
        <Typography variant="body2" sx={{ color: error ? COLOR_ERROR_1 : COLOR_TEXT_2, mb: 2 }}>
          {VERIFIED_MARKETS_MESSAGE}
        </Typography>
      )}
      {hotelData && (
        <>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {supplyRows.map((item, index) => (
          <Grid item xs={12} sm={6} key={item.label}>
            <Box
              sx={{
                backgroundColor: '#fff',
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                border: '1px solid rgba(0,0,0,0.06)',
                p: 2,
                height: '100%',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Typography
                  variant="body1"
                  sx={{ color: COLOR_TEXT_1, fontWeight: 500 }}
                >
                  {item.label}
                </Typography>
                <InfoButton tooltipContent={item.tooltip} />
              </Box>
              <Box component="span" sx={{ display: 'block', ...blurSx }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: supplyCardValueColors[index] ?? COLOR_TEXT_2,
                    lineHeight: 1.2,
                    fontSize: { xs: '1.5rem', sm: '1.75rem' },
                    mb: 0.5,
                  }}
                >
                  {item.hotelCount} hotels
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                  <Typography
                    variant="body2"
                    sx={{ color: COLOR_TEXT_2, fontSize: '0.8125rem', opacity: 0.9, flex: 1, minWidth: 0 }}
                  >
                    {item.perThousand} per 1,000 Airbnb listings (global avg: {item.nationalAvg})
                  </Typography>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1,
                      py: 0.5,
                      borderRadius: 2,
                      backgroundColor: item.highLow === 'High' ? COLOR_SUCCESS_BACKGROUNG : COLOR_ERROR_BACKGROUNG,
                      color: item.highLow === 'High' ? COLOR_SUCCESS_1 : COLOR_ERROR_1,
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                  >
                    {item.highLow === 'High' ? (
                      <ThumbUpRoundedIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <ThumbDownRoundedIcon sx={{ fontSize: 16 }} />
                    )}
                    {item.highLow}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
        ))}
        {/* Third card: Hotel Demand */}
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              backgroundColor: '#fff',
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              border: '1px solid rgba(0,0,0,0.06)',
              p: 2,
              height: '100%',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography
                variant="body1"
                sx={{ color: COLOR_TEXT_1, fontWeight: 500 }}
              >
                Hotel Demand
              </Typography>
            </Box>
            <Box component="span" sx={{ display: 'block', ...blurSx }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: COLOR_PRIMARY_2,
                  lineHeight: 1.2,
                  fontSize: { xs: '1.5rem', sm: '1.75rem' },
                  mb: 0.5,
                }}
              >
                {hotelData.hotel_demand_level != null
                  ? `${roundInt(hotelData.hotel_demand_level)}%`
                  : '—'}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: COLOR_TEXT_2, fontSize: '0.875rem', mb: 1 }}
              >
                Hotel demand level
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap' }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    borderRadius: 2,
                    backgroundColor:
                      (hotelData.hotel_demand_level ?? 0) >=
                      (hotelData.hotel_demand_level_global_avg ?? 0)
                        ? COLOR_SUCCESS_BACKGROUNG
                        : COLOR_ERROR_BACKGROUNG,
                    color:
                      (hotelData.hotel_demand_level ?? 0) >=
                      (hotelData.hotel_demand_level_global_avg ?? 0)
                        ? COLOR_SUCCESS_1
                        : COLOR_ERROR_1,
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                  }}
                >
                  {(hotelData.hotel_demand_level ?? 0) >=
                  (hotelData.hotel_demand_level_global_avg ?? 0)
                    ? 'Strong market'
                    : 'Weak market'}
                </Box>
                <InfoButton tooltipContent={demandLevelTooltip} />
              </Box>
            </Box>
          </Box>
        </Grid>
        {/* Fourth card: STR vs Hotel */}
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              backgroundColor: '#fff',
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              border: '1px solid rgba(0,0,0,0.06)',
              p: 2,
              height: '100%',
            }}
          >
          <Typography
            variant="body1"
            sx={{ color: COLOR_TEXT_1, fontWeight: 500, mb: 1 }}
          >
            STR vs Hotel
          </Typography>
          <Box component="span" sx={{ display: 'block', ...blurSx }}>
            <Typography
              sx={{
                fontWeight: 700,
                color: COLOR_PRIMARY_2,
                lineHeight: 1.2,
                fontSize: { xs: '1.5rem', sm: '1.75rem' },
                mb: 1,
              }}
            >
              {hotelData.str_vs_hotel}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: COLOR_TEXT_2,
                fontSize: '0.8125rem',
                opacity: 0.75,
                fontStyle: 'italic',
              }}
            >
              {hotelData.str_vs_hotel === 'STR' ? STR_OUTPERFORMING : HOTEL_OUTPERFORMING}
            </Typography>
          </Box>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, my: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: COLOR_PRIMARY_2,
            fontSize: '1rem',
          }}
        >
          Hotel Demand Pressure by lead time
        </Typography>
        <InfoButton tooltipContent={HOTEL_DEMAND_PRESSURE_TOOLTIP} />
      </Box>

      <Box sx={{ width: '100%', height: 280, ...blurSx }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={availabilityData}
            margin={{ top: 24, right: 24, left: 0, bottom: 20 }}
            barCategoryGap="30%"
            barGap={0}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLOR_GREY_200} />
            <XAxis
              dataKey="leadTime"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fontFamily: 'Roboto', fill: COLOR_TEXT_2, fontWeight: 500 }}
            />
            <YAxis
              domain={[0, 100]}
              tickFormatter={(v) => `${v}%`}
              tickLine={false}
              axisLine={{ stroke: COLOR_TEXT_2, strokeDasharray: '4 4' }}
              tick={{ fontSize: 12, fontFamily: 'Roboto', fill: COLOR_TEXT_2, fontWeight: 500 }}
              label={{
                value: 'Availability (%)',
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle', fontSize: 12, fontWeight: 600, fill: COLOR_TEXT_2 },
              }}
            />
            <Tooltip content={<CustomTooltip />} cursor={false} />
            <Legend
              wrapperStyle={{ paddingTop: 12 }}
              iconType="rect"
              iconSize={10}
              payload={[
                { value: 'Market', type: 'rect', color: MARKET_BAR_COLOR },
                { value: 'Global median', type: 'line', color: NATIONAL_MEDIAN_COLOR },
              ]}
              formatter={(value) => (
                <span style={{ fontSize: 12, color: COLOR_TEXT_2, fontWeight: 500 }}>{value}</span>
              )}
            />
            {/* Single bar: market availability (same axis); no value labels on bars */}
            <Bar
              dataKey="market"
              name="Market"
              fill={MARKET_BAR_COLOR}
              radius={[6, 6, 0, 0]}
              barSize={32}
              maxBarSize={48}
            />
            {/* Benchmark: horizontal bar (short line) at global median per bar */}
            <Customized
              component={(p: any) => (
                <BenchmarkHorizontalBars
                  {...p}
                  data={availabilityData}
                  stroke={NATIONAL_MEDIAN_COLOR}
                />
              )}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
        </>
      )}
    </AIOverviewCard>
  );
};

export default React.memo(HotelSupplyAndAvailabilityCard);
